import {Date as TopolaDate, DateOrRange, DateRange} from "../topola"
import {IntlShape} from "react-intl"
import {DateOrYear, InputMode} from "../datepicker/date-or-year"


const DATE_QUALIFIERS = new Map([
    ["abt", "about"],
    ["cal", "calculated"],
    ["est", "estimated"]
])

export function toDateOrYear(date: TopolaDate): DateOrYear {
    if (date.year !== undefined && date.month === undefined && date.day === undefined)
        return new DateOrYear({ mode: InputMode.YEAR, year: date.year })
    else
        return new DateOrYear({ mode: InputMode.DATE, date: toDateObject(date) })
}

export function toDateObject(date: TopolaDate): Date {
    return new Date(
        date.year !== undefined ? date.year! : 0,
        date.month !== undefined ? date.month! - 1 : 0,
        date.day !== undefined ? date.day! : 1
    )
}

export function formatDate(date: TopolaDate, intl: IntlShape): string {
    const hasDay = date.day !== undefined
    const hasMonth = date.month !== undefined
    const hasYear = date.year !== undefined

    if (!hasDay && !hasMonth && !hasYear) {
        return date.text || ""
    }

    const dateObject = toDateObject(date)
    const translatedQualifier = formatDateQualifier(date.qualifier, intl)
    const formatOptions: Intl.DateTimeFormatOptions = {
        day: hasDay ? "numeric" : undefined,
        month: hasMonth ? "long" : undefined,
        year: hasYear ? "numeric" : undefined
    }
    const translatedDate = new Intl.DateTimeFormat(
        intl.locale,
        formatOptions
    ).format(dateObject)

    return [translatedQualifier, translatedDate]
        .filter((dateElement) => dateElement)
        .join(" ")
}

function formatDateRage(dateRange: DateRange, intl: IntlShape) {
    const fromDate = dateRange.from
    const toDate = dateRange.to
    const translatedFromDate = fromDate && formatDate(fromDate, intl)
    const translatedToDate = toDate && formatDate(toDate, intl)
    if (translatedFromDate && translatedToDate)
        return intl.formatMessage({ id: "date.between" }, { from: translatedFromDate, to: translatedToDate })
    if (translatedFromDate)
        return intl.formatMessage({ id: "date.after" }, { from: translatedFromDate })
    if (translatedToDate)
        return intl.formatMessage({ id: "date.before" }, { to: translatedToDate })
    return ""
}

export function formatDateQualifier(qualifier: string | undefined, intl: IntlShape): string {
    const lowerCaseQualifier = qualifier && qualifier.toLowerCase()
    return (
        (lowerCaseQualifier &&
        intl.formatMessage({id: `date.${lowerCaseQualifier}`, defaultMessage: DATE_QUALIFIERS.get(lowerCaseQualifier) || lowerCaseQualifier})) || ""
    )
}

export function formatDateOrRange(dateOrRange: DateOrRange | undefined, intl: IntlShape): string {
    if (!dateOrRange)
        return ""
    if (dateOrRange.date)
        return formatDate(dateOrRange.date, intl)
    if (dateOrRange.dateRange)
        return formatDateRage(dateOrRange.dateRange, intl)
    return ""
}

export function compareTopolaDates(date1: TopolaDate | undefined, date2: TopolaDate | undefined): number {
    if (!date1 || !date1.year || !date2 || !date2.year)
        return 0
    if (date1.year !== date2.year)
        return date1.year - date2.year
    if (!date1.month || !date2.month)
        return 0
    if (date1.month !== date2.month)
        return date1.month - date2.month
    if (date1.day && date2.day && date1.day !== date2.day)
        return date1.month - date2.month
    return 0
}

export function compareDates(firstDateOrRange: DateOrRange | undefined, secondDateOrRange: DateOrRange | undefined): number {
    const date1 = firstDateOrRange
        && (firstDateOrRange.date
        || (firstDateOrRange.dateRange && (firstDateOrRange.dateRange.from || firstDateOrRange.dateRange.to)))
    const date2 = secondDateOrRange
        && (secondDateOrRange.date
        || (secondDateOrRange.dateRange && (secondDateOrRange.dateRange.from || secondDateOrRange.dateRange.to)))
    return compareTopolaDates(date1, date2)
}